// features/bookmarks/BookmarksPage.tsx — Bookmarks page (auth-gated).
import { useMemo, useState } from 'react';
import { NetworkLinks } from '@/features/networks/NetworkLinks';
import { Navigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useBookmarks, useSession } from '@/hooks';
import { useRightRail, RailGroup, RailPanel } from '@/app/shell/RightRail';
import { HydrationState } from '@/components/HydrationState';
import { useHydrationPending } from '@/hooks/useGlobalHydration';
import { BookmarkTabs } from './BookmarkTabs';
import { BookmarkSortMenu } from './BookmarkSortMenu';
import { BookmarkCard } from './BookmarkCard';
import { FoldersPanel } from './panels/FoldersPanel';
import { BookmarksOverviewPanel } from './panels/BookmarksOverviewPanel';
import { RecentlySavedPanel } from './panels/RecentlySavedPanel';
import { ManageFoldersDialog } from './ManageFoldersDialog';
import { filterBookmarks } from './filterBookmarks';
import type { TypeFilter, SortId } from './meta';

const Title = styled.h1`
  margin: 0 0 14px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;
const Feed = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const Empty = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border-radius: ${({ theme }) => theme.radius.md};
`;

export function BookmarksPage() {
  const { isAuthenticated, currentUser } = useSession();
  const { bookmarks, folders, remove, moveBookmark, createFolder, renameFolder, deleteFolder } =
    useBookmarks();

  const pending = useHydrationPending('bookmarks');
  const [activeType, setActiveType] = useState<TypeFilter>('all');
  const [activeFolder, setActiveFolder] = useState('all');
  const [activeSort, setActiveSort] = useState<SortId>('recent');
  const [manageOpen, setManageOpen] = useState(false);

  const visible = useMemo(
    () => filterBookmarks(bookmarks, activeType, activeFolder, activeSort),
    [bookmarks, activeType, activeFolder, activeSort],
  );

  const rightRail = useMemo(
    () => (
      <RailGroup pageKey="bookmarks">
        <RailPanel id="folders" label="Folders" required>
          <FoldersPanel
            bookmarks={bookmarks}
            folders={folders}
            activeFolder={activeFolder}
            onFolderChange={setActiveFolder}
            onManage={() => setManageOpen(true)}
          />
        </RailPanel>
        <RailPanel id="overview" label="Bookmarks overview">
          <BookmarksOverviewPanel bookmarks={bookmarks} folders={folders} />
        </RailPanel>
        <RailPanel id="recent" label="Recently saved">
          <RecentlySavedPanel bookmarks={bookmarks} />
        </RailPanel>
      </RailGroup>
    ),
    [bookmarks, folders, activeFolder],
  );
  useRightRail(rightRail);

  if (!isAuthenticated || !currentUser) {
    return <Navigate to={`/signin?next=${encodeURIComponent('/bookmarks')}`} replace />;
  }

  return (
    <section>
      <Title>Bookmarks</Title>
      <NetworkLinks destination="bookmarks" />
      <p>
        <Link to="/discussions?tab=bookmarks">Private notes on saved posts and comments</Link>
      </p>
      <Toolbar>
        <BookmarkTabs active={activeType} onChange={setActiveType} />
        <BookmarkSortMenu active={activeSort} onChange={setActiveSort} />
      </Toolbar>
      <Feed>
        {pending ? (
          <HydrationState resource="bookmarks" label="your bookmarks" />
        ) : visible.length === 0 ? (
          <Empty>No bookmarks here yet.</Empty>
        ) : (
          visible.map((b) => (
            <BookmarkCard
              key={b.id}
              bookmark={b}
              folders={folders}
              onRemove={(id) => void remove(id)}
              onMove={(id, folderId) => void moveBookmark(id, folderId)}
            />
          ))
        )}
      </Feed>
      <ManageFoldersDialog
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        folders={folders}
        onCreate={(name) => void createFolder(name)}
        onRename={(id, name) => void renameFolder(id, name)}
        onDelete={(id) => void deleteFolder(id)}
      />
    </section>
  );
}
